import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Button, Select, MultiSelect, LoadingBar } from "@perfectserve/library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe, faChevronDown, faArrowLeft, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import "@perfectserve/css-styles-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, } from "recharts";
import { usePlatformMetrics } from "../hooks/usePlatformMetrics";
import { getInteractionColor } from "../types/platformMetrics";
const PlatformMetrics = ({ onClose, className = "" }) => {
    const { fetchPlatformVolumeMetrics } = usePlatformMetrics();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [platformData, setPlatformData] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState("30d");
    const [visibleTypes, setVisibleTypes] = useState(new Set());
    const [selectedInteractionTypes, setSelectedInteractionTypes] = useState([]);
    const [yesterdayData, setYesterdayData] = useState([]);
    const [showAllYesterdayActions, setShowAllYesterdayActions] = useState(false);
    const loadPlatformMetrics = useCallback(async (period, selectedTypes) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchPlatformVolumeMetrics(period, selectedTypes);
            setPlatformData(data);
            if (!selectedTypes) {
                setVisibleTypes(new Set(data.interactionTypes));
                setSelectedInteractionTypes(data.interactionTypes);
            }
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load platform metrics");
        }
        finally {
            setLoading(false);
        }
    }, []);
    const loadYesterdayData = useCallback(async () => {
        try {
            const yesterdayPlatformData = await fetchPlatformVolumeMetrics("24h");
            const yesterdayMetrics = processYesterdayData(yesterdayPlatformData);
            setYesterdayData(yesterdayMetrics);
        }
        catch (err) {
            console.error("❌ Error loading yesterday data:", err);
        }
    }, []);
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await loadYesterdayData();
            const data = await fetchPlatformVolumeMetrics(selectedPeriod);
            setPlatformData(data);
            setVisibleTypes(new Set(data.interactionTypes));
            setSelectedInteractionTypes(data.interactionTypes);
        }
        catch (err) {
            console.error("❌ Error loading platform data:", err);
            setError(err instanceof Error ? err.message : "Failed to load data");
        }
        finally {
            setLoading(false);
        }
    }, [selectedPeriod, loadYesterdayData]);
    useEffect(() => {
        loadData();
    }, [loadData]);
    const handlePeriodChange = (newPeriod) => {
        setSelectedPeriod(newPeriod);
        loadPlatformMetrics(newPeriod);
    };
    const handleInteractionTypesChange = (selectedTypes) => {
        setSelectedInteractionTypes(selectedTypes);
        setVisibleTypes(new Set(selectedTypes));
    };
    const processYesterdayData = (platformData) => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        let yesterdayTotals = {};
        let totalInteractions = 0;
        platformData.weeklyMetrics.forEach((week) => {
            const weekStart = new Date(week.weekStart);
            const weekEnd = new Date(week.weekEnd);
            if (yesterday >= weekStart && yesterday <= weekEnd) {
                platformData.interactionTypes.forEach((type) => {
                    const value = week[type] || 0;
                    yesterdayTotals[type] = (yesterdayTotals[type] || 0) + value;
                    totalInteractions += value;
                });
            }
        });
        const pieData = Object.entries(yesterdayTotals)
            .filter(([_, value]) => value > 0)
            .map(([type, value]) => ({
            name: type,
            value: value,
            percentage: totalInteractions > 0 ? (value / totalInteractions) * 100 : 0,
            color: getInteractionColor(type),
        }))
            .sort((a, b) => b.value - a.value);
        return pieData;
    };
    const formatChartData = (weeklyMetrics) => {
        return weeklyMetrics.map((week) => {
            const formattedWeek = {
                week: new Date(week.weekStart).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: new Date(week.weekStart).getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
                }),
                weekStart: week.weekStart,
            };
            if (platformData) {
                platformData.interactionTypes.forEach((type) => {
                    formattedWeek[type] = week[type] || 0;
                });
            }
            return formattedWeek;
        });
    };
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
            return (_jsxs("div", { style: {
                    backgroundColor: "white",
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    minWidth: "200px",
                }, children: [_jsx("p", { style: {
                            margin: "0 0 8px 0",
                            fontWeight: "bold",
                            fontSize: "14px",
                        }, children: `Week of ${label}` }), _jsx("p", { style: { margin: "0 0 8px 0", fontWeight: "600", color: "#374B94" }, children: `Total: ${total.toLocaleString()} interactions` }), payload
                        .filter((entry) => entry.value > 0)
                        .sort((a, b) => b.value - a.value)
                        .map((entry, index) => (_jsx("p", { style: {
                            margin: "2px 0",
                            color: entry.color,
                            fontSize: "13px",
                        }, children: `${entry.name}: ${entry.value.toLocaleString()}` }, index)))] }));
        }
        return null;
    };
    const PieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (_jsxs("div", { style: {
                    backgroundColor: "white",
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    minWidth: "180px",
                }, children: [_jsx("p", { style: {
                            margin: "0 0 8px 0",
                            fontWeight: "bold",
                            fontSize: "14px",
                            color: data.color,
                        }, children: data.name }), _jsxs("p", { style: { margin: "0", fontSize: "13px" }, children: [_jsx("strong", { children: data.value.toLocaleString() }), " interactions"] }), _jsxs("p", { style: { margin: "0", fontSize: "13px", color: "#666" }, children: [data.percentage.toFixed(1), "% of total"] })] }));
        }
        return null;
    };
    return (_jsxs("div", { className: `interactions-container platform-metrics-container ${className}`, children: [_jsx("div", { children: _jsxs("div", { className: "pt-20 pb-20", style: { display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }, children: [_jsx(Button, { onClick: onClose || (() => { }), icon: faArrowLeft, children: "Back to Dashboard" }), _jsxs("div", { className: "platform-metrics-title-section", children: [_jsx(FontAwesomeIcon, { icon: faGlobe, className: "platform-metrics-title-icon" }), _jsx("h2", { className: "platform-metrics-title", children: "Platform Volume Trends" })] }), _jsx("div", { style: { minWidth: "150px" } })] }) }), _jsxs("div", { className: "interactions-card", style: {
                    margin: "20px 0",
                    padding: "24px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }, children: [_jsx("div", { children: platformData && (_jsxs("div", { className: "platform-metrics-controls-row", style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px" }, children: [_jsxs("div", { className: "platform-metrics-total-section", children: [_jsx("span", { className: "platform-metrics-total-label", children: "Total Interactions:" }), _jsx("span", { className: "platform-metrics-total-value", children: platformData.totalInteractions.toLocaleString() })] }), _jsxs("div", { style: {
                                        flex: "1",
                                        maxWidth: "500px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "12px",
                                    }, children: [_jsx("label", { className: "fs-14 fw-600 text-gray-800", style: { whiteSpace: "nowrap" }, children: "Interaction Types" }), _jsx(MultiSelect, { options: platformData.interactionTypes.map((type) => ({
                                                value: type,
                                                label: type,
                                            })), selectedValues: selectedInteractionTypes, onChange: handleInteractionTypesChange, placeholder: "Select interaction types...", searchPlaceholder: "Search types..." })] }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [_jsx("span", { style: { fontSize: "14px", fontWeight: "600", color: "#374B94" }, children: "Period:" }), _jsx(Select, { value: selectedPeriod, onChange: (value) => handlePeriodChange(value), options: [
                                                { value: "24h", label: "Last 24 Hours" },
                                                { value: "7d", label: "Last 7 Days" },
                                                { value: "30d", label: "Last 30 Days" },
                                                { value: "90d", label: "Last 90 Days" },
                                                { value: "180d", label: "Last 180 Days" },
                                                { value: "1y", label: "Last Year" },
                                                { value: "2y", label: "Last 2 Years" },
                                            ] })] })] })) }), loading && _jsx(LoadingBar, {}), _jsxs("div", { id: "platform-metrics-content", className: "platform-metrics-content", children: [error && (_jsxs("div", { className: "platform-metrics-error", children: [_jsx("strong", { children: "Error:" }), " ", error] })), platformData && !loading && (_jsx("div", { className: "platform-metrics-chart", children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(AreaChart, { data: formatChartData(platformData.weeklyMetrics), children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "#e0e0e0" }), _jsx(XAxis, { dataKey: "week", stroke: "#666", fontSize: 12, tick: { fill: "#666" } }), _jsx(YAxis, { stroke: "#666", fontSize: 12, tick: { fill: "#666" }, tickFormatter: (value) => value.toLocaleString() }), _jsx(Tooltip, { content: _jsx(CustomTooltip, {}) }), platformData.interactionTypes
                                                .filter((type) => visibleTypes.has(type))
                                                .map((type) => (_jsx(Area, { type: "monotone", dataKey: type, stackId: "1", stroke: getInteractionColor(type), fill: getInteractionColor(type), fillOpacity: 0.7, strokeWidth: 1 }, type)))] }) }) }))] }), yesterdayData.length > 0 ? (_jsxs("div", { className: "p-20", children: [_jsx("h3", { className: "platform-metrics-yesterday-title", children: "Platform Metrics - Yesterday" }), _jsxs("div", { className: "d-flex", children: [_jsx("div", { className: "platform-metrics-yesterday-chart", children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(PieChart, { children: [_jsx(Pie, { data: yesterdayData, cx: "50%", cy: "50%", innerRadius: 60, outerRadius: 120, paddingAngle: 0, dataKey: "value", stroke: "none", children: yesterdayData.map((entry, index) => (_jsx(Cell, { fill: entry.color, stroke: "none" }, `cell-${index}`))) }), _jsx(Tooltip, { content: _jsx(PieTooltip, {}) })] }) }) }), _jsx("div", { className: "platform-metrics-yesterday-stats", children: _jsxs("div", { className: "platform-metrics-summary-card", children: [_jsx("h4", { className: "platform-metrics-summary-title", children: "Yesterday's Summary" }), _jsx("div", { className: "platform-metrics-summary-total", children: yesterdayData.reduce((sum, item) => sum + item.value, 0).toLocaleString() }), _jsx("div", { className: "platform-metrics-summary-label", children: "Total Interactions" }), _jsxs("div", { className: "platform-metrics-actions-list", children: [_jsx("div", { className: "platform-metrics-actions-title", children: "Top Actions:" }), (showAllYesterdayActions ? yesterdayData : yesterdayData.slice(0, 8)).map((item) => (_jsxs("div", { className: "platform-metrics-action-item", children: [_jsxs("span", { className: "platform-metrics-action-name", children: [_jsx("div", { className: "platform-metrics-type-color", style: {
                                                                                backgroundColor: item.color,
                                                                            } }), _jsx("span", { className: "platform-metrics-action-name-text", children: item.name })] }), _jsxs("span", { className: "platform-metrics-action-value", children: [item.value.toLocaleString(), " (", item.percentage.toFixed(1), "%)"] })] }, item.name))), yesterdayData.length > 8 && (_jsx(Button, { onClick: () => setShowAllYesterdayActions(!showAllYesterdayActions), icon: showAllYesterdayActions ? faChevronUp : faChevronDown, iconPosition: "left", fullWidth: true, className: "platform-metrics-expand-button", children: showAllYesterdayActions ? "Show Less" : `Show All (${yesterdayData.length - 8} more)` }))] })] }) })] })] })) : (!loading && (_jsxs("div", { className: "platform-metrics-yesterday-section", children: [_jsx("h3", { className: "platform-metrics-yesterday-title", children: "Platform Metrics - Yesterday" }), _jsx("div", { className: "platform-metrics-no-data", style: {
                                    padding: "40px",
                                    textAlign: "center",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    margin: "20px 0",
                                    color: "#6c757d",
                                }, children: _jsx("p", { style: { margin: "0", fontSize: "16px" }, children: "There are no results for yesterday, check back after 7AM EST." }) })] })))] })] }));
};
export default PlatformMetrics;
//# sourceMappingURL=PlatformMetrics.js.map